import { apiPath, openAuthedFile } from '../../services/authedFile';
import type { InvoiceDetail } from '../../types/invoices';
import { InvoiceSheet } from './InvoiceSheet';

interface OrderInvoiceSectionProps {
  invoice: InvoiceDetail;
}

/**
 * Embedded invoice block for the portal Order Detail tab. Renders the shared
 * branded {@link InvoiceSheet} inside a titled, bordered card so the order's
 * most-recent Sage invoice reads as part of the "order story". Kit-component
 * and pure-comment lines are already filtered server-side, so what shows here
 * reconciles with the invoice totals.
 *
 * When the sales site's emailed copy of the Sage-printed invoice exists
 * (`invoice.sage_pdf`), the header offers it as a one-click open in a new tab;
 * the embedded sheet stays the portal rendering so the order story does not
 * pay for a PDF fetch on every open.
 */
export const OrderInvoiceSection: React.FC<OrderInvoiceSectionProps> = ({
  invoice,
}) => {
  const isCreditMemo = invoice.kind === 'credit_memo';
  const sagePdf = invoice.sage_pdf;
  return (
    <div data-testid="order-invoice-section" style={{ marginTop: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <h3
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#374151',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            margin: 0,
          }}
        >
          {isCreditMemo ? 'Credit Memo' : 'Invoice'}
        </h3>
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          #{invoice.invoice_no}
        </span>
        {sagePdf && (
          <button
            type="button"
            data-testid="order-invoice-sage-pdf-open"
            data-action="order_invoice_sage_pdf_open"
            data-target-invoice-no={invoice.invoice_no}
            data-outcome="open"
            onClick={() => {
              void openAuthedFile(apiPath(sagePdf.preview_url)).catch(() =>
                window.alert('Could not open the Sage invoice PDF. Please try again.'),
              );
            }}
            title={sagePdf.filename}
            style={{
              marginLeft: 'auto',
              fontSize: 12,
              color: '#1d4ed8',
              padding: '4px 10px',
              borderRadius: 6,
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              cursor: 'pointer',
            }}
          >
            Open Sage PDF ↗
          </button>
        )}
      </div>
      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <InvoiceSheet invoice={invoice} />
      </div>
    </div>
  );
};
